import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';

import { AppState } from '../store';
import {
    selectOutdatedHorizons$,
} from '../store/selectors/masterdata.selectors';
import {
    OutdatedHorizon,
    OutdatedHorizonUnit,
} from '../models/masterdata';

const MINUTES_BY_UNIT:
    Record<OutdatedHorizonUnit, number> = {
        [OutdatedHorizonUnit.Minute]: 1,
        [OutdatedHorizonUnit.Hour]: 60,
        [OutdatedHorizonUnit.Day]: 24 * 60,
    };

@Injectable({
    providedIn: 'root',
})
export class OutdatedHorizonService {
    /**
     * Fallback used when a provider does not have
     * an explicit configuration.
     */
    private readonly _defaultOutdatedHorizon:
        Omit<OutdatedHorizon, 'provider'> = {
            unit: OutdatedHorizonUnit.Day,
            value: 30,
        };

    /**
     * Provider-keyed lookup cache derived from NgRx state.
     */
    private readonly _outdatedHorizonMap =
        new Map<string, OutdatedHorizon>();

    /**
     * Provides the latest valid configurations to existing
     * consumers that still use this observable.
     */

    constructor(
        private _store: Store<AppState>,
    ) {
        this._store
            .select(selectOutdatedHorizons$)
            .subscribe(
                (
                    outdatedHorizons:
                        OutdatedHorizon[],
                ): void => {
                    this._setOutdatedHorizons(
                        outdatedHorizons ?? [],
                    );
                },
            );
    }

    /**
     * Get the explicit configuration for a provider.
     *
     * @param provider Provider name.
     * @return Explicit configuration or undefined.
     */
    public getConfiguredOutdatedHorizon(
        provider?: string,
    ): OutdatedHorizon | undefined {
        const normalizedProvider =
            this._normalizeProvider(
                provider,
            );

        if (!normalizedProvider) {
            return undefined;
        }

        return this._outdatedHorizonMap.get(
            normalizedProvider,
        );
    }

    /**
     * Get the effective Horizon for a provider.
     *
     * The 30-day fallback is returned when no explicit
     * provider configuration exists.
     *
     * @param provider Provider name.
     * @return Effective provider configuration.
     */
    public getOutdatedHorizon(
        provider?: string,
    ): OutdatedHorizon {
        const configuredHorizon =
            this.getConfiguredOutdatedHorizon(
                provider,
            );

        if (configuredHorizon) {
            return configuredHorizon;
        }

        return {
            provider: provider ?? '',
            ...this._defaultOutdatedHorizon,
        };
    }

    /**
     * Convert the effective provider Horizon to milliseconds.
     *
     * @param provider Provider name.
     * @return Outdated threshold in milliseconds.
     */
    public getOutdatedThresholdMs(
        provider?: string,
    ): number {
        const horizon =
            this.getOutdatedHorizon(
                provider,
            );

        const minutesPerUnit =
            MINUTES_BY_UNIT[horizon.unit] ??
            MINUTES_BY_UNIT[
                OutdatedHorizonUnit.Day
            ];

        const validValue =
            Number.isFinite(horizon.value) &&
            horizon.value > 0
                ? horizon.value
                : this
                    ._defaultOutdatedHorizon
                    .value;

        return (
            validValue *
            minutesPerUnit *
            60 *
            1000
        );
    }

    /**
     * Return the latest explicit configurations.
     *
     * @return Sorted provider configurations.
     */
    public getCachedOutdatedHorizons():
        OutdatedHorizon[] {

        return this._getCachedHorizons();
    }

    /**
     * Replace the lookup cache from NgRx state.
     *
     * @param outdatedHorizons Provider configurations.
     * @return {void}
     */
    private _setOutdatedHorizons(
        outdatedHorizons:
            OutdatedHorizon[],
    ): void {
        this._outdatedHorizonMap.clear();

        for (
            const outdatedHorizon
            of outdatedHorizons
        ) {
            if (
                !this._isValidOutdatedHorizon(
                    outdatedHorizon,
                )
            ) {
                continue;
            }

            this._storeOutdatedHorizon(
                outdatedHorizon,
            );
        }
    }

    /**
     * Add or replace one provider configuration.
     *
     * @param outdatedHorizon Provider configuration.
     * @return {void}
     */
    private _storeOutdatedHorizon(
        outdatedHorizon:
            OutdatedHorizon,
    ): void {
        const normalizedProvider =
            this._normalizeProvider(
                outdatedHorizon.provider,
            );

        if (!normalizedProvider) {
            return;
        }

        this._outdatedHorizonMap.set(
            normalizedProvider,
            {
                ...outdatedHorizon,
                provider:
                    outdatedHorizon
                        .provider
                        .trim(),
            },
        );
    }


    /**
     * Convert the lookup Map into a sorted array.
     *
     * @return Sorted provider configurations.
     */
    private _getCachedHorizons():
        OutdatedHorizon[] {

        return Array.from(
            this._outdatedHorizonMap
                .values(),
        )
            .map(
                (
                    horizon:
                        OutdatedHorizon,
                ) => ({
                    ...horizon,
                }),
            )
            .sort(
                (
                    first:
                        OutdatedHorizon,
                    second:
                        OutdatedHorizon,
                ) =>
                    first.provider
                        .localeCompare(
                            second.provider,
                        ),
            );
    }

    /**
     * Validate a Horizon configuration.
     *
     * @param outdatedHorizon Configuration to validate.
     * @return True when the configuration is valid.
     */
    private _isValidOutdatedHorizon(
        outdatedHorizon:
            OutdatedHorizon,
    ): boolean {
        if (
            !outdatedHorizon
                ?.provider
                ?.trim()
        ) {
            return false;
        }

        if (
            !Object
                .values(
                    OutdatedHorizonUnit,
                )
                .includes(
                    outdatedHorizon.unit,
                )
        ) {
            return false;
        }

        return (
            Number.isFinite(
                outdatedHorizon.value,
            ) &&
            outdatedHorizon.value > 0
        );
    }

    /**
     * Normalize a provider name for lookup.
     *
     * @param provider Provider name.
     * @return Normalized provider key.
     */
    private _normalizeProvider(
        provider?: string,
    ): string {
        return (
            provider ?? ''
        )
            .trim()
            .toLowerCase();
    }
}